/**
 * Cart domain — pure, immutable operations over a table's drink/food order.
 * Prices are integer minor units (satang), matching @jod/schemas Money.
 */

export interface CartItem {
  id: string;
  name: string;
  nameTh?: string;
  /** Unit price in satang. */
  priceMinor: number;
  iconName?: string;
  quantity: number;
  note?: string;
}

export type CartItemInput = Omit<CartItem, "quantity">;

/** Add an item (or merge into the existing line, summing quantity). */
export function addItem(cart: readonly CartItem[], item: CartItemInput, quantity = 1): CartItem[] {
  if (quantity <= 0) return [...cart];
  const existing = cart.find((c) => c.id === item.id);
  if (existing) {
    return cart.map((c) =>
      c.id === item.id ? { ...c, quantity: c.quantity + quantity, note: item.note ?? c.note } : c,
    );
  }
  return [...cart, { ...item, quantity }];
}

/** Set an absolute quantity. Zero or less removes the line. */
export function setQuantity(cart: readonly CartItem[], id: string, quantity: number): CartItem[] {
  if (quantity <= 0) return cart.filter((c) => c.id !== id);
  return cart.map((c) => (c.id === id ? { ...c, quantity } : c));
}

export function removeItem(cart: readonly CartItem[], id: string): CartItem[] {
  return cart.filter((c) => c.id !== id);
}

/** Total number of physical items in the cart. */
export function cartCount(cart: readonly CartItem[]): number {
  return cart.reduce((sum, c) => sum + c.quantity, 0);
}

/** Order subtotal in satang. */
export function cartTotalMinor(cart: readonly CartItem[]): number {
  return cart.reduce((sum, c) => sum + c.priceMinor * c.quantity, 0);
}
